/**
 * Preview side-effect module (the "Data preview" feature). The
 * Preview button (in `<export-panel>`) calls `runPreview()`; the `<data-preview>`
 * card renders from these atoms. Split out so the trigger and the view stay in
 * separate, single-job files (the §5 estimate.ts pattern) rather than wiring a
 * DOM event between two light-DOM siblings.
 */

import { Atom } from '../../state/store.ts';
import { buildExportRequest } from '../../state/request.ts';
import { preview } from '../../api/endpoints.ts';
import { hideError } from '../../ui/error-box.ts';
import type { PreviewResponse } from '../../api/types.ts';
import { userMessage } from '../../api/errors.ts';

/** Whether the preview card is shown (revealed on the first Preview click). */
export const previewVisible = new Atom<boolean>(false);
export const previewLoading = new Atom<boolean>(false);
export const previewData = new Atom<PreviewResponse | null>(null);
export const previewError = new Atom<string | null>(null);

/**
 * Run a `/preview` for the current request and publish the result to the atoms.
 * No-ops when ship/dates aren't ready (button is disabled in that case anyway).
 */
export async function runPreview(): Promise<void> {
  const req = buildExportRequest();
  if (!req) return;

  previewVisible.set(true);
  previewLoading.set(true);
  previewError.set(null);
  previewData.set(null);

  try {
    const data = await preview(req);
    previewData.set(data);
    hideError();
  } catch (e) {
    previewError.set(userMessage(e));
  } finally {
    previewLoading.set(false);
  }
}
